import { EnvironmentState } from 'src/domains/layouts/state/environmentState/EnvironmentState';
import { autorun, makeObservable } from 'mobx';
import { ExternalApi } from 'src/domains/layouts/state/externalApi/ExternalApi';
import { createContext } from 'src/utils/createContext';
import { Session } from 'src_common/sdk/session';
import { AppSportsBookState } from 'src/domains/sportsbook/state/AppSportsBook.state';
import { AppCasinoState } from 'src/domains/casino/state/AppCasino.state';
import { slug } from 'src/utils/deburr';
import { TrpcClient } from './TrpcClient';
import { Common } from 'src/domains/common/Common';
import { UniverseType } from 'src_common/common/universe';
import { UniverseModuleType } from 'src/domains/common/universes.type';
import { EnvVariables } from 'src/domains/common/contextStore/EnvVariables';
import { AccountTabsBuilderState } from 'src/domains/players/webview/components/Account/accountDrawer/AccountTabsBuilder.state';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { AppPlayersState } from 'src/domains/players/state/AppPlayers.state';
import { DebugPanelState } from 'src/domains/layouts/webview/components/debugPanel/DebugPanelState';

export class AppState {
    /** !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! */
    /** * Please do not move, External API should stay in AppState directly */
    /** !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! */
    public readonly externalApi: ExternalApi; //layouts
    /** !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! */
    /** !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! */
    /** !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!! */

    /** * Please do not move until new version of Mobile App - contact Layouts team LT-848 */
    public readonly env: EnvironmentState; // moved

    /** SportsBookState must remain in AppState because it is used by external operators. It is intended to be removed in the future. */
    public readonly appSportsBookState: AppSportsBookState;
    /** PlayersState must remain in AppState because it is used by external operators. It is intended to be removed in the future. */
    public readonly appPlayersState: AppPlayersState;

    /** CasinoState must remain in AppState because it is used by external operators. It is intended to be removed in the future. */
    public readonly appCasinoState: AppCasinoState;

    public readonly accountTabsBuilderState: AccountTabsBuilderState;

    public readonly debugPanel: DebugPanelState;

    // TODO: TO Remove after finish refactor all domains states
    public readonly languagesState: LanguagesState;

    public constructor(public readonly common: Common) {
        makeObservable(this);
        this.languagesState = LanguagesState.get(common);
        this.externalApi = ExternalApi.get(common);

        //Please do not move until new version of Mobile App - contact Layouts team LT-848
        /**
         * @deprecated - please use EnvironmentState.get(common)
         */
        this.env = EnvironmentState.get(common);

        this.appCasinoState = AppCasinoState.get(common);

        // --------------- appPlayersState ---------------
        this.appPlayersState = AppPlayersState.get(common);
        // --------------- end appPlayersState ---------------

        // --------------- appSportsBookState ---------------
        this.appSportsBookState = AppSportsBookState.get(common);
        // --------------- end appSportsBookState ---------------

        this.debugPanel = DebugPanelState.get(common);

        StarRouter.get(common).onChangeCurrentView((_prevCurrentView, nextCurrentView) => {
            if (nextCurrentView?.name === 'event') {
                const eventId = nextCurrentView.id;

                autorun((dispose) => {
                    const eventModel = this.appSportsBookState.models.getEvent(eventId);

                    if (eventModel !== null) {
                        dispose.dispose();

                        const currentView = StarRouter.get(common).currentView;

                        //if the user is still on the event page
                        if (currentView?.name === 'event' && currentView.id === eventId) {
                            //if the event is a race, redirect to the race page
                            if (eventModel.sport === 'horseracing' || eventModel.sport === 'greyhoundracing') {
                                if (eventModel.antePost) {
                                    StarRouter.get(common).redirectTo({
                                        name: 'sport',
                                        nameType: 'races',
                                        id: eventModel.sport,
                                        type: 'ante-post',
                                        event: {
                                            id: eventId,
                                            slug: slug(eventModel.name),
                                        },
                                    });
                                } else {
                                    StarRouter.get(common).redirectToRaceCard(
                                        eventModel.id2,
                                        eventModel.sport,
                                        eventModel.competition
                                    );
                                }
                            }
                        }
                    }
                });
            }

            if (nextCurrentView?.name === 'racecard' && nextCurrentView.selected !== null) {
                const eventId = nextCurrentView.selected;

                autorun((dispose) => {
                    const eventModel = this.appSportsBookState.models.getEvent(eventId);

                    if (eventModel !== null) {
                        dispose.dispose();

                        const currentView = StarRouter.get(common).currentView;

                        //if the user is still on the racecard page
                        if (currentView?.name === 'racecard' && currentView.selected === eventId) {
                            //if the event is a race, redirect to the race page
                            if (eventModel.sport === 'horseracing' || eventModel.sport === 'greyhoundracing') {
                                if (eventModel.eventType === 'outrights') {
                                    const isSpecials = eventModel.getTag('specials') === 'yes';
                                    StarRouter.get(common).redirectTo({
                                        name: 'sport',
                                        nameType: 'races',
                                        id: eventModel.sport,
                                        type: isSpecials ? 'specials' : null,
                                        event: {
                                            id: eventId,
                                            slug: slug(eventModel.name),
                                        },
                                    });
                                }
                                if (eventModel.antePost) {
                                    StarRouter.get(common).redirectTo({
                                        name: 'sport',
                                        nameType: 'races',
                                        id: eventModel.sport,
                                        type: 'ante-post',
                                        event: {
                                            id: eventId,
                                            slug: slug(eventModel.name),
                                        },
                                    });
                                }
                            }
                        }
                    }
                });
            }
        });

        this.accountTabsBuilderState = new AccountTabsBuilderState(this.common);
    }

    public static createForStorybook(universe: UniverseType, universeModule: UniverseModuleType): AppState {
        const { themeColors, features } = universeModule;
        const envVariables = EnvVariables.createForStorybook(universe);
        const session = Session.startFakeSession();
        const websiteBaseUrl = '';
        const startingUrl = '';

        const isBrowser = true;

        const common = new Common(
            startingUrl,
            envVariables,
            session,
            isBrowser,
            new TrpcClient(websiteBaseUrl, isBrowser, session, false),
            themeColors,
            features,
            '',
            null,
            websiteBaseUrl
        );
        return new AppState(common);
    }
}

const { AppContext, useContext } = createContext<AppState>('appState');

/**
 * @deprecated - Don't use context. This way is old because we want to move to microfrontends
 */
export const Provider = AppContext.Provider;

export const useAppStateContext = (): AppState => {
    return useContext();
};
